import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquareText, Send, Sparkles, X } from "lucide-react";
import { api } from "@/lib/site";

const greetings = [
  "Hi — I'm Ask Ajay, the AI concierge for aksahni.com. What are you exploring today?",
  "Hello! Curious about Ajay's training programs, certifications, or engagement model? Ask away.",
];

export default function AskAjayChatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const sessionRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      const greet = greetings[Math.floor(Math.random() * greetings.length)];
      setMessages([{ role: "assistant", text: greet }]);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setSending(true);
    try {
      const r = await api.post("/chat", {
        session_id: sessionRef.current,
        message: text,
      });
      sessionRef.current = r.data.session_id;
      setMessages((m) => [...m, { role: "assistant", text: r.data.reply }]);
    } catch {
      setMessages((m) => [
        ...m,
        { role: "assistant", text: "I'm having trouble reaching the assistant. Please try again, or email connect@aksahni.com." },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="chatbot-toggle"
        aria-label="Ask Ajay"
        className="fixed bottom-20 right-6 z-40 group inline-flex items-center gap-2 pl-4 pr-5 py-3 rounded-full bg-white text-black font-medium shadow-2xl hover:bg-slate-100 transition-colors"
      >
        <span className="h-7 w-7 grid place-items-center rounded-full bg-gradient-to-br from-blue-500 to-cyan-400 text-black">
          <Sparkles size={14} />
        </span>
        <span className="text-sm">Ask Ajay</span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            data-testid="chatbot-panel"
            className="fixed bottom-36 right-6 z-40 w-[min(92vw,400px)] h-[min(70vh,560px)] bg-[#0A0C13] border border-white/10 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 glass">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 grid place-items-center rounded-full bg-gradient-to-br from-blue-500 to-cyan-400 text-black">
                  <MessageSquareText size={16} />
                </div>
                <div>
                  <div className="font-display font-semibold text-white text-sm">Ask Ajay</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    AI concierge · live
                  </div>
                </div>
              </div>
              <button
                aria-label="Close"
                onClick={() => setOpen(false)}
                className="text-slate-400 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {messages.map((m, i) => (
                <Bubble key={i} role={m.role} text={m.text} />
              ))}
              {sending && <Bubble role="assistant" text="…" pulse />}
            </div>

            <form onSubmit={send} className="border-t border-white/10 p-3 flex items-center gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about programs, pricing, scheduling…"
                data-testid="chatbot-input"
                className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/60"
              />
              <button
                type="submit"
                disabled={!input.trim() || sending}
                data-testid="chatbot-send"
                className="h-10 w-10 grid place-items-center rounded-full bg-white text-black hover:bg-slate-200 disabled:opacity-50 transition-colors"
                aria-label="Send"
              >
                <Send size={15} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function Bubble({ role, text, pulse }) {
  const mine = role === "user";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
          mine
            ? "bg-blue-600 text-white rounded-br-sm"
            : "bg-white/5 border border-white/10 text-slate-100 rounded-bl-sm"
        } ${pulse ? "animate-pulse" : ""}`}
      >
        {text}
      </div>
    </div>
  );
}
